/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import * as stylex from '@stylexjs/stylex';
import { type ForwardedRef, Children, createContext, forwardRef, isValidElement } from 'react';
import type { TagRenderProps } from 'react-aria-components';
import { type ContextValue, type TagProps as TagCoreProps, Button as ButtonCore, composeRenderProps, DEFAULT_SLOT, Provider, Tag as TagCore, useContextProps } from 'react-aria-components';
import { CloseStrokeIcon } from '../../icons/close-stroke/close-stroke.icon';
import { $ } from '../../tokens.stylex';
import { applyStylesWithRenderProps } from '../../utils/apply-styles-with-render-props/apply-styles-with-render-props.util';
import { mergeStyles } from '../../utils/merge-styles/merge-styles.util';
import { BUTTON_SLOTS } from '../button/button.component';
import { IconContext } from '../icon/icon.component';
import type { NumberBadgeProps } from '../number-badge/number-badge.component';
import { NumberBadgeContext } from '../number-badge/number-badge.component';
import { type TextProps, Text, TextContext } from '../text/text.component';

interface TagProps extends Omit<TagCoreProps, 'className' | 'style'> {
  /** The variant of the tag. (Default: primary) */
  variant?: keyof typeof variantVariants;
  /** The size of the tag. (Default: 32) */
  size?: keyof typeof sizeVariants;
  /** Whether the button is rounded. (Default: false) */
  isRounded?: boolean;
  style?: stylex.StyleXStyles;
}

const TagContext = createContext<ContextValue<TagProps, HTMLDivElement>>({});

const Tag = forwardRef(({ style: styleProp, ...mergedProps }: TagProps, ref: ForwardedRef<HTMLDivElement>) => {
  [mergedProps, ref] = useContextProps(mergedProps, ref, TagContext);

  const { style: contextStyle, isRounded = false, variant = 'primary', size = 32, ...coreProps } = mergedProps as TagProps;

  let textValue = typeof mergedProps.children === 'string' ? mergedProps.children : undefined;

  const style = mergeStyles(contextStyle, styleProp);

  return (
    <TagCore
      ref={ref}
      textValue={textValue}
      {...applyStylesWithRenderProps<TagRenderProps>((state) => stylex.props(
        styles.base,
        variantVariants[variant],
        sizeVariants[size],
        state.allowsRemoving && styles.removable,
        isRounded && styles.rounded,
        coreProps.isDisabled && styles.disabled,
        style))}
      {...coreProps}>
      {composeRenderProps(mergedProps.children, (children, renderProps) => {
        const evaluatedChildren = children ?? renderProps.defaultChildren;
        const isTextOnly = Children.toArray(evaluatedChildren).every(c => !isValidElement(c));

        const contentColor = (
          renderProps.isDisabled && 'quaternary' ||
          variant === 'primary' &&
          (
            (renderProps.isHovered || renderProps.isPressed) && 'primaryHover' ||
            'white'
          ) ||
          variant === 'primaryMono' && 'inverted' ||
          variant === 'secondary' && 'black' ||
          variant === 'tertiary' && 'tertiaryHover' ||
          variant === 'quaternary' &&
          (
            (renderProps.isHovered || renderProps.isPressed) && 'primary' ||
            'secondary'
          ) ||
          variant === 'alternative' && 'black' ||
          variant === 'ghost' && (
            (renderProps.isHovered || renderProps.isPressed) && 'primary' ||
            'tertiary'
          ) ||
          'interactive'
        );

        const numberBadgeColor = (
          (renderProps.isDisabled && 'disabled') ||
          (['primary', 'primaryMono', 'tertiary'].includes(variant) && 'informational') ||
          'neutral'
        );

        return (
          <Provider values={[
            [TextContext, {
              slot: null!,
              style: [textStyles.base, style],
              size: TEXT_SIZES[size],
              color: renderProps.isDisabled ? 'buttonContentDisabled' : contentColor
            }],
            [IconContext, {
              slots: {
                [DEFAULT_SLOT]: {
                  style: [iconStyles.base, style],
                  color: contentColor,
                },
                [BUTTON_SLOTS.LEFT]: {
                  size: LEFT_ICON_SIZE[size],
                  color: contentColor,
                  style: [iconStyles.base, accessoryLeftStyles[size], style],
                },
                [BUTTON_SLOTS.RIGHT]: {
                  size: RIGHT_ICON_SIZE[size],
                  color: contentColor,
                  style: [iconStyles.base, accessoryRightStyles[size], style],
                }
              }
            }],
            [NumberBadgeContext, {
              slots: {
                [BUTTON_SLOTS.LEFT]: {
                  style: [accessoryLeftStyles[size], style],
                  size: NUMBER_BADGE_SIZES[size],
                  isPastel: false,
                  color: numberBadgeColor,
                },
                [BUTTON_SLOTS.RIGHT]: {
                  style: [accessoryRightStyles[size], style],
                  size: NUMBER_BADGE_SIZES[size],
                  isPastel: false,
                  color: numberBadgeColor,
                }
              }
            }]
          ]}>
            {isTextOnly ? <Text>{evaluatedChildren}</Text> : evaluatedChildren}
            {renderProps.allowsRemoving && (
              <ButtonCore slot="remove" {...stylex.props(removeButtonStyles.base)}>
                <CloseStrokeIcon slot={BUTTON_SLOTS.RIGHT} />
              </ButtonCore>)}
          </Provider>
        );
      })}
    </TagCore>
  );
});

const TEXT_SIZES: Record<keyof typeof sizeVariants, TextProps['size']> = {
  24: 12,
  28: 12,
  32: 13,
  40: 14,
  48: 16,
  52: 16,
  64: 20,
};

const LEFT_ICON_SIZE: Record<keyof typeof sizeVariants, number> = {
  24: 12,
  28: 12,
  32: 16,
  40: 16,
  48: 20,
  52: 20,
  64: 24,
};

const RIGHT_ICON_SIZE: Record<keyof typeof sizeVariants, number> = {
  24: 12,
  28: 12,
  32: 12,
  40: 12,
  48: 16,
  52: 16,
  64: 16,
};

const NUMBER_BADGE_SIZES: Record<keyof typeof sizeVariants, NumberBadgeProps['size']> = {
  24: 16,
  28: 16,
  32: 16,
  40: 20,
  48: 20,
  52: 24,
  64: 24,
};


const styles = stylex.create({
  base: {
    borderStyle: 'solid',
    borderWidth: 0,
    textDecoration: 'none',
    alignItems: 'center',
    boxSizing: 'border-box',
    cursor: 'pointer',
    display: 'inline-flex',
    isolation: 'isolate',
    justifyContent: 'center',
    position: 'relative',
    textWrap: 'nowrap',
    transitionDuration: '0.2s',
    transitionProperty: 'color, background-color, border',
    transitionTimingFunction: 'easy-in-out',
    userSelect: 'none',
  },
  rounded: { borderRadius: 999 },
  disabled: {
    borderColor: 'transparent',
    backgroundColor: $.buttonSurfaceDisabled,
    cursor: 'not-allowed',
  },
  removable: {
    cursor: 'default',
  }
});

// :hover is used instead of data-hovered because it depends on the selection mode, and we always want to apply hover styles.      
const variantVariants = stylex.create({
  primary: {
    backgroundColor: {
      default: $.buttonSurfacePrimary,
      ':is(:hover, [data-pressed])': $.buttonSurfacePrimaryHover,
    },
  },
  primaryMono: {
    backgroundColor: {
      default: $.buttonSurfacePrimaryMono,
      ':is(:hover, [data-pressed])': $.buttonSurfacePrimaryMonoHover,
    },
  },
  secondary: {
    backgroundColor: {
      default: $.buttonSurfaceSecondary,
      ':is(:hover, [data-pressed])': $.buttonSurfaceSecondaryHover,
    },
  },
  tertiary: {
    backgroundColor: {
      default: $.buttonSurfaceTertiary,
      ':is(:hover, [data-pressed])': $.buttonSurfaceTertiaryHover,
    },
  },
  quaternary: {
    borderColor: {
      default: $.border10,
      ':is(:hover, [data-pressed])': 'transparent',
    },
    borderWidth: 1,
    backgroundColor: {
      default: 'transparent',
      ':is(:hover, [data-pressed])': $.buttonSurfaceQuaternaryHover,
    },
  },
  alternative: {
    backgroundColor: {
      default: $.buttonSurfaceAlternative,
      ':is(:hover, [data-pressed])': $.buttonSurfaceAlternativeHover,
    },
  },
  ghost: {
    backgroundColor: {
      default: 'transparent',
      ':is(:hover, [data-pressed])': $.opacity04,
    }
  },
});

const sizeVariants = stylex.create({
  24: {
    borderRadius: 6,
    paddingInline: 8,
    height: 24,
  },
  28: {
    borderRadius: 6,
    paddingInline: 12,
    height: 28,
  },
  32: {
    borderRadius: 8,
    paddingInline: 12,
    height: 32,
  },
  40: {
    borderRadius: 8,
    paddingInline: 16,
    height: 40,
  },
  48: {
    borderRadius: 10,
    paddingInline: 20,
    height: 48,
  },
  52: {
    borderRadius: 10,
    paddingInline: 28,
    height: 52,
  },
  64: {
    borderRadius: 14,
    paddingInline: 32,
    height: 64,
  },
});

const textStyles = stylex.create({
  base: {
    transitionDuration: '0.2s',
    transitionProperty: 'color',
    transitionTimingFunction: 'easy-in-out',
  },
});

const iconStyles = stylex.create({
  base: {
    transitionDuration: '0.2s',
    transitionProperty: 'stroke, fill',
    transitionTimingFunction: 'easy-in-out',
  },
});

const accessoryLeftStyles = stylex.create({
  24: { marginRight: 6 },
  28: { marginRight: 6 },
  32: { marginRight: 8 },
  40: { marginRight: 6 },
  48: { marginRight: 8 },
  52: { marginRight: 10 },
  64: { marginRight: 12 },
});

const accessoryRightStyles = stylex.create({
  24: { marginLeft: 4 },
  28: { marginLeft: 4 },
  32: { marginLeft: 4 },
  40: { marginLeft: 6 },
  48: { marginLeft: 6 },
  52: { marginLeft: 8 },
  64: { marginLeft: 8 },
});

const removeButtonStyles = stylex.create({
  base: {
    padding: 0,
    borderStyle: 'none',
    backgroundColor: 'inherit',
    cursor: {
      default: 'pointer',
      ':is([data-disabled])': 'not-allowed',
    },
  }
});

export { Tag, TagContext, type TagProps };
